import { Button, Tooltip } from '@sgme/ui';
import { counterpartApiConnector } from 'apiConnector/counterpart/counterpart';
import { BlotterErrorIcon } from 'components/Blotters/BlotterErrorIcon';
import { ProductFilterComponent } from 'components/Blotters/Quotes/Filter/ProductFilter';
import { CompactSelector } from 'components/shared/compact-selector';
import { useSalesCounterpartButton } from 'components/shared/compact-selector/use-toggle-button';
import { MonoDatePickerWithDefaultPreset } from 'components/shared/DatePicker';
import { SalesCounterPartResult } from 'components/shared/SalesCounterPart/SalesCounterPartResult';
import { SelectSearch } from 'components/shared/SelectSearch/SelectSearch';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { addonFormsRequested } from 'state/addonManagementForm/addonManagementForm.action';
import { isQuotingAddon } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import { addonManagementFilterChanged } from 'state/workspaces/addonManagement/addonManagement.actions';
import type { AddonManagementFilters } from 'state/workspaces/addonManagement/addonManagement.model';
import { initialAddonManagementFilters } from 'state/workspaces/addonManagement/addonManagement.model';
import {
  getFiltersToPersist,
  getIsTradesFetchSuccessful,
} from 'state/workspaces/addonManagement/addonManagement.selectors';
import styled from 'styled-components';
import { httpClient } from 'utils/http/httpClient';

import { AddonManagementViewSelector } from './AddonManagementViewSelector';

const StyledDiv = styled.div`
  width: 315px;
  z-index: 4;
`;

const counterpartApi = counterpartApiConnector(httpClient);

export const AddonManagementToolbar: FC = () => {
  const dispatch = useDispatch<ThunkDispatch>();

  const { formatMessage } = useIntl();
  const filters = useSelector(getFiltersToPersist);
  const salesCounterpartPlaceholderIntlKey = 'addonManagement.filter.salesCounterpart.placeholder';
  const isTradesFetchSuccessful = useSelector(getIsTradesFetchSuccessful);
  const isQuoting = useSelector(isQuotingAddon);

  const applyFilters = useCallback(
    (filters: AddonManagementFilters, name: string) => {
      logAnalytics.trackEvent('Addon Management Workspace', 'Update filter', name);
      dispatch(addonManagementFilterChanged({ ...filters }));
    },
    [dispatch],
  );

  const changeSalesCounterpart = useCallback(
    (id: string | undefined, label = '') => {
      applyFilters(
        {
          ...filters,
          client: id ? { id, label } : undefined,
        },
        'Client',
      );
    },
    [applyFilters, filters],
  );

  const onSearch = useCallback((text: string) => {
    return counterpartApi.fetchSalesCounterparts(text);
  }, []);

  const copyToClipboard = useCopyTextToClipboard();

  const resetAddonRequest = () => dispatch(addonFormsRequested());
  const { label, className } = useSalesCounterpartButton(filters.client, false, salesCounterpartPlaceholderIntlKey);
  return (
    <div data-e2e="addon-management-toolbar" className="d-flex flex-row bg-lvl1 border-top">
      <div className="d-flex flex-grow-1 px-16px align-items-center">
        <h5 className="lh-1">
          <FormattedMessage id="addonManagement.toolbar.title" />
        </h5>
        {isTradesFetchSuccessful === false && (
          <BlotterErrorIcon message={formatMessage({ id: 'addonManagement.view.fetchError' })} />
        )}
      </div>
      <ProductFilterComponent
        value={filters.productFilter!}
        filterPredicate={product => product !== 'accumulator'}
        onSelect={productFilter =>
          applyFilters(
            {
              ...filters,
              productFilter,
            },
            'Product Type',
          )
        }
      />

      <div className="btn-group border-start border-opacity-40">
        <CompactSelector
          openOnHover
          isDisabled={isQuoting}
          renderButton={(ref, onClick) => (
            <CompactSelector.ToggleButton
              e2ePrefix="client"
              ref={ref}
              onClick={onClick}
              label={label}
              disabled={isQuoting}
              className={className}
            />
          )}
          onSelectedItemChanged={client => changeSalesCounterpart(client?.id, client?.label)}
          selectedValue={filters.client ? { id: filters.client?.id, label: filters.client?.label ?? '' } : null}
          renderContent={(onSelectedChanged, selected) => (
            <SelectSearch
              autofocus
              getSearchResult={onSearch}
              onValueSelected={onSelectedChanged}
              resultRenderer={SalesCounterPartResult}
            >
              {selected && (
                <button
                  className="btn btn-outline-secondary btn-icon-end text-nowrap ms-2"
                  onClick={() => copyToClipboard(`${filters.client?.label!} (${filters.client?.id!})`)}
                >
                  <em className="icon ">file_copy</em>
                  <FormattedMessage id="client.copy" />
                </button>
              )}
            </SelectSearch>
          )}
        ></CompactSelector>
      </div>

      <AddonManagementViewSelector />

      <StyledDiv className="filter-to-date-picker form-group align-self-end">
        <MonoDatePickerWithDefaultPreset
          id="addon-management-filter-date-start"
          e2e="addon-management-filter-date-start"
          minDate={initialAddonManagementFilters.issueDateOnAndAfter}
          disabled={isQuoting}
          allowWeekend={false}
          isClearable
          size="xl"
          value={filters.issueDateOnAndAfter ?? null}
          onChange={date => applyFilters({ ...filters, issueDateOnAndAfter: date ?? undefined }, 'Issue date')}
          format="MMM d, yyyy → ∞"
          defaultValue={initialAddonManagementFilters.issueDateOnAndAfter}
          hidePresets
        />
      </StyledDiv>

      <div className="ms-auto py-2 align-self-end">
        <Tooltip
          position="top"
          contents={
            <div data-e2e="tooltip-element">
              <FormattedMessage id="addonManagement.button.refresh" />
            </div>
          }
        >
          <div data-e2e="tooltip-trigger" className="tooltip-trigger">
            <Button purpose="flat" color="secondary" disabled={isQuoting} onClick={resetAddonRequest}>
              <em className="icon icon-sm">refresh</em>
            </Button>
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
